import React from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import Icon from 'components/Icon'
import ConfirmModal from 'components/ConfirmModal'
import { GetSpacesOverviewQuery, SpaceType } from 'generated/graphql'
import { PickNode } from 'graphql/types'
import { useDeleteItem, useEventSlug } from 'hooks'
import { _, colors } from 'utils'
import { addModal } from 'features/modals/modalSlice'
import { getEventData } from '../../events/eventSlice'
import { deleteSpace, updateSpacePublished } from '../graphql/mutation'
import { getSpaceIconName } from '../utils'

const S = {
    Row: styled.div<{ isOpen: boolean }>`
        width: 100%;
        margin-left: -27px;
        padding: 12px 12px 12px 27px;
        display: grid;
        grid-template-columns: 1fr 1fr 100px;
        align-items: center;
        font-size: 14px;
        color: ${colors.grayTitleOpacity(0.8)};
        &:hover > .space-actions > * {
            opacity: 1;
        }
        ${({ isOpen }) => !isOpen && css`
            background-color: #FAFAFA!important;
            & > .space-type {
                opacity: 0.5;
            }
            & > .space-actions span:last-child {
                opacity: 1;
            }
        `}
    `,
    Actions: styled.div`
        display: flex;
        align-items: center;
        justify-content: flex-end;
        & > * {
            opacity: 0;
        }
    `,
    SpaceType: styled.span`
        text-transform: capitalize;
    `,
}

interface Props {
    space: PickNode<GetSpacesOverviewQuery, 'spaces'>
    fetchData: () => Promise<void>
}

const SpaceRow = ({ space, fetchData }: Props) => {
    const eventSlug = useEventSlug()
    const dispatch = useDispatch()
    const isOpen = space.published
    const updatePublished = () => {
        updateSpacePublished(space.id.toString(), !isOpen)
        fetchData()
        dispatch(getEventData(eventSlug))
    }
    const deleteFn = async (row: SpaceType) => {
        await deleteSpace(row.id)
        dispatch(getEventData(eventSlug))
    }
    const { showDeleteConfirmModal } = useDeleteItem(
        deleteFn, fetchData, {
            title: _('spaces.form.delete_space_title'),
            description: _('spaces.form.delete_space_description')
        }
    )

    const showConfirmModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <ConfirmModal
                    close={close}
                    title={
                        isOpen
                            ? _('spaces.form.close_space_title_modal')
                            : _('spaces.form.open_space_title_modal')
                    }
                    confirmDescription={
                        isOpen
                            ? _('spaces.form.close_space_description_modal')
                            : _('spaces.form.open_space_description_modal')
                    }
                    variantConfirm="primary"
                    onConfirm={updatePublished}
                />
            ),
            shouldNotWrap: true
        }))
    }

    return (
        <S.Row isOpen={isOpen}>
            <span>{space.name}</span>
            <S.SpaceType className="space-type">
                <Icon
                    icon={getSpaceIconName(space.type)}
                    size={18}
                    style={{ marginRight: 10 }}
                />
                {space.type}
            </S.SpaceType>
            <S.Actions className="space-actions">
                <Link to={`/${eventSlug}/${space.type}/${space.slug}`}>
                    <Icon icon="edit" size={18} style={{ marginRight: 12 }} />
                </Link>
                <Icon
                    icon="trash"
                    size={18}
                    onClick={() => showDeleteConfirmModal(space)}
                    style={{ marginRight: 12 }}
                />
                <Icon
                    icon={isOpen ? 'view' : 'viewOff'}
                    color={isOpen ? colors.primary : colors.iconOpacity(0.5)}
                    size={18}
                    onClick={showConfirmModal}
                />
            </S.Actions>
        </S.Row>
    )
}

export default SpaceRow
